/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { isMobile } from '@/utils/utils';
import { leaveCardView } from '@/store/slices/room';
import { fetchAlertDetail } from '@/api/alert';
import {
  AlertDetailResponse,
  AlertTypeId,
  Severity,
  ALERT_TYPES,
  ALERT_TYPE_DESCRIPTIONS,
  SEVERITY_LABELS,
  SEVERITY_COLORS,
  ROLE_LABELS,
} from '@/types/alert';
import style from './index.module.less';

type ViewState = 'loading' | 'ready' | 'error' | 'empty';

function EmotionAlert() {
  const dispatch = useDispatch();
  const [viewState, setViewState] = useState<ViewState>('loading');
  const [data, setData] = useState<AlertDetailResponse | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<AlertTypeId[]>([]);
  const [selectedSeverities, setSelectedSeverities] = useState<Severity[]>([]);
  const [showAllTurns, setShowAllTurns] = useState(false);
  const [showTypeDescriptions, setShowTypeDescriptions] = useState(false);

  const loadAlertData = useCallback(async () => {
    setViewState('loading');
    try {
      const conversationId = 'demo-conversation-001';
      const result = await fetchAlertDetail(conversationId);

      if (!result || !result.overall) {
        setViewState('empty');
        return;
      }

      setData(result);
      setViewState('ready');
    } catch (error) {
      console.error('Failed to load alert data:', error);
      setViewState('error');
    }
  }, []);

  useEffect(() => {
    loadAlertData();
  }, [loadAlertData]);

  const handleBack = () => {
    dispatch(leaveCardView());
  };

  const handleRetry = () => {
    loadAlertData();
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const toggleTypeFilter = (typeId: AlertTypeId) => {
    setSelectedTypes((prev) =>
      prev.includes(typeId) ? prev.filter((t) => t !== typeId) : [...prev, typeId]
    );
  };

  const toggleSeverityFilter = (severity: Severity) => {
    setSelectedSeverities((prev) =>
      prev.includes(severity) ? prev.filter((s) => s !== severity) : [...prev, severity]
    );
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setSelectedSeverities([]);
  };

  const handleCopySuggestion = (text: string) => {
    navigator.clipboard.writeText(text);
    // 可以添加 toast 提示
  };

  const handleExportJSON = () => {
    if (!data) return;
    const dataStr = JSON.stringify(data, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `alert_${data.conversation_id}_${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 过滤后的数据
  const filteredTurns = useMemo(() => {
    if (!data) return [];
    if (selectedTypes.length === 0) return data.related_turns;

    return data.related_turns.filter((turn) =>
      turn.hit_types.some((type) => selectedTypes.includes(type))
    );
  }, [data, selectedTypes]);

  const displayedTurns = showAllTurns ? filteredTurns : filteredTurns.slice(0, 3);

  // 绘制半圆进度条的 SVG
  const renderScoreGauge = (score: number) => {
    const percentage = score / 10;
    const angle = 180 * percentage;
    const radius = 80;
    const strokeWidth = 12;
    const center = 100;

    const startAngle = 180;
    const endAngle = 180 + angle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const x1 = center + radius * Math.cos(startRad);
    const y1 = center + radius * Math.sin(startRad);
    const x2 = center + radius * Math.cos(endRad);
    const y2 = center + radius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    const color =
      score >= 8 ? '#d32f2f' : score >= 6 ? '#ff5722' : score >= 4 ? '#ff9800' : '#4caf50';

    return (
      <svg width="200" height="120" viewBox="0 0 200 120" className={style.gaugeChart}>
        {/* 背景半圆 */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke="#e0e0e0"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* 进度半圆 */}
        <path
          d={`M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}`}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
        {/* 分数文字 */}
        <text x="100" y="90" textAnchor="middle" className={style.scoreText}>
          {score.toFixed(1)}
        </text>
        <text x="100" y="110" textAnchor="middle" className={style.scoreLabel}>
          / 10
        </text>
      </svg>
    );
  };

  // Loading State
  if (viewState === 'loading') {
    return (
      <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
        <div className={style.header}>
          <button className={style.backButton} onClick={handleBack}>
            ‹ 返回
          </button>
          <h2 className={style.title}>对话预警</h2>
        </div>
        <div className={style.content}>
          <div className={style.loadingState}>
            <div className={style.spinner} />
            <p>加载中...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (viewState === 'error') {
    return (
      <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
        <div className={style.header}>
          <button className={style.backButton} onClick={handleBack}>
            ‹ 返回
          </button>
          <h2 className={style.title}>对话预警</h2>
        </div>
        <div className={style.content}>
          <div className={style.errorState}>
            <div className={style.errorIcon}>⚠️</div>
            <h3>加载失败</h3>
            <p>无法获取预警数据，请稍后重试</p>
            <button className={style.retryButton} onClick={handleRetry}>
              重试
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty State
  if (viewState === 'empty') {
    return (
      <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
        <div className={style.header}>
          <button className={style.backButton} onClick={handleBack}>
            ‹ 返回
          </button>
          <h2 className={style.title}>对话预警</h2>
        </div>
        <div className={style.content}>
          <div className={style.emptyState}>
            <div className={style.emptyIcon}>✓</div>
            <h3>当前无预警</h3>
            <p>对话状态良好，暂无需要关注的预警信息</p>
          </div>
        </div>
      </div>
    );
  }

  // Ready State - 主要内容
  if (!data) return null;

  return (
    <div className={`${style.wrapper} ${isMobile() ? style.mobile : ''}`}>
      {/* Header */}
      <div className={style.header}>
        <button className={style.backButton} onClick={handleBack}>
          ‹ 返回
        </button>
        <h2 className={style.title}>对话预警</h2>
        <button className={style.exportButton} onClick={handleExportJSON}>
          导出JSON
        </button>
      </div>

      {/* Content */}
      <div className={style.content}>
        <div className={style.container}>
          {/* A. 顶部概览卡 */}
          <div className={style.summaryCard}>
            <div className={style.summaryMain}>
              <div className={style.summaryInfo}>
                <div className={style.subjectName}>对象：{data.subject_name}</div>
                <h3 className={style.riskStatus}>
                  当前风险状态：
                  <span
                    className={style.severityBadge}
                    style={{ backgroundColor: SEVERITY_COLORS[data.overall.severity] }}
                  >
                    {SEVERITY_LABELS[data.overall.severity]}
                  </span>
                </h3>
                <div className={style.detectedTime}>
                  检测时间：{formatTimestamp(data.overall.detected_at)}
                </div>
                <div className={style.typesHit}>
                  {data.overall.types_hit.map((typeId) => (
                    <span key={typeId} className={style.typeChip}>
                      {ALERT_TYPES[typeId]}
                    </span>
                  ))}
                </div>
              </div>
              <div className={style.scoreGauge}>{renderScoreGauge(data.overall.score_10)}</div>
            </div>
          </div>

          {/* 筛选器 */}
          <div className={style.filterCard}>
            <div className={style.filterSection}>
              <span className={style.filterLabel}>预警类型：</span>
              <div className={style.filterChips}>
                {(Object.keys(ALERT_TYPES) as unknown as AlertTypeId[]).map((typeId) => (
                  <button
                    key={typeId}
                    className={`${style.filterChip} ${
                      selectedTypes.includes(typeId) ? style.active : ''
                    }`}
                    onClick={() => toggleTypeFilter(typeId)}
                  >
                    {ALERT_TYPES[typeId]}
                  </button>
                ))}
              </div>
            </div>
            <div className={style.filterSection}>
              <span className={style.filterLabel}>严重程度：</span>
              <div className={style.filterChips}>
                {(['low', 'medium', 'high', 'critical'] as Severity[]).map((severity) => (
                  <button
                    key={severity}
                    className={`${style.filterChip} ${
                      selectedSeverities.includes(severity) ? style.active : ''
                    }`}
                    onClick={() => toggleSeverityFilter(severity)}
                    style={{
                      borderColor: selectedSeverities.includes(severity)
                        ? SEVERITY_COLORS[severity]
                        : undefined,
                    }}
                  >
                    {SEVERITY_LABELS[severity]}
                  </button>
                ))}
              </div>
            </div>
            {(selectedTypes.length > 0 || selectedSeverities.length > 0) && (
              <button className={style.clearFilters} onClick={clearFilters}>
                清除筛选
              </button>
            )}
          </div>

          {/* B. 触发原因卡 */}
          <div className={style.reasonsCard}>
            <h3 className={style.cardTitle}>预警触发原因</h3>
            {data.reasons.length > 0 ? (
              <div className={style.reasonsList}>
                {data.reasons.map((reason, idx) => (
                  <div key={idx} className={style.reasonItem}>
                    <span className={style.reasonBullet}>•</span>
                    <span>{reason}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className={style.emptyText}>暂无明确触发原因（模型置信度不足）</p>
            )}
          </div>

          {/* C. 相关聊天记录卡 */}
          <div className={style.chatCard}>
            <h3 className={style.cardTitle}>相关聊天记录</h3>
            <div className={style.chatList}>
              {displayedTurns.map((turn) => (
                <div key={turn.turn_id} className={style.chatItem}>
                  <div className={style.chatHeader}>
                    <span className={`${style.roleTag} ${style[turn.role]}`}>
                      {ROLE_LABELS[turn.role]}
                    </span>
                    <span className={style.chatTime}>{formatTimestamp(turn.timestamp)}</span>
                  </div>
                  <div className={style.chatText}>{turn.redacted ? '内容已脱敏' : turn.text}</div>
                  {turn.hit_types.length > 0 && (
                    <div className={style.chatTypes}>
                      {turn.hit_types.map((typeId) => (
                        <span key={typeId} className={style.chatTypeChip}>
                          {ALERT_TYPES[typeId]}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {filteredTurns.length > 3 && (
              <button className={style.toggleButton} onClick={() => setShowAllTurns(!showAllTurns)}>
                {showAllTurns ? '收起' : `展开全部 (${filteredTurns.length} 条)`}
              </button>
            )}
          </div>

          {/* D. 建议措施卡 */}
          <div className={style.suggestionsCard}>
            <h3 className={style.cardTitle}>建议措施</h3>
            <div className={style.suggestionsList}>
              {data.suggestions.map((suggestion) => (
                <div key={suggestion.id} className={style.suggestionItem}>
                  <div className={style.suggestionHeader}>
                    <h4 className={style.suggestionTitle}>{suggestion.title}</h4>
                    <button
                      className={style.copyButton}
                      onClick={() => handleCopySuggestion(suggestion.text)}
                    >
                      复制
                    </button>
                  </div>
                  <p className={style.suggestionText}>{suggestion.text}</p>
                  <div className={style.suggestionMeta}>适用：{suggestion.when_to_use}</div>
                </div>
              ))}
            </div>
          </div>

          {/* E. 类型说明卡 */}
          <div className={style.policyCard}>
            <div className={style.policyHeader}>
              <h3 className={style.cardTitle}>预警类型说明</h3>
              <button
                className={style.toggleButton}
                onClick={() => setShowTypeDescriptions(!showTypeDescriptions)}
              >
                {showTypeDescriptions ? '收起' : '展开'}
              </button>
            </div>
            {showTypeDescriptions && (
              <div className={style.policyList}>
                {(Object.keys(ALERT_TYPES) as unknown as AlertTypeId[]).map((typeId) => (
                  <div key={typeId} className={style.policyItem}>
                    <span className={style.policyType}>
                      {typeId}. {ALERT_TYPES[typeId]}
                    </span>
                    <span className={style.policyDesc}>{ALERT_TYPE_DESCRIPTIONS[typeId]}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 元信息 */}
          <div className={style.metaInfo}>
            <span>模型版本：{data.meta.model_version}</span>
            <span>置信度：{(data.meta.confidence * 100).toFixed(0)}%</span>
            <span>更新时间：{formatTimestamp(data.meta.last_updated_at)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmotionAlert;
